import React, { useEffect, useMemo, useState } from "react";

// Material-UI Components
import Checkbox from "@material-ui/core/Checkbox";

// External Libraries
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

// Local Assets
import recurringIcon from "../.././../../../assets/icons/recurring.svg";
import rightArrow from "../.././../../../assets/icons/right-arrow.svg";

// Custom Components
import { formatISODateToLongDate } from "../../../../../helper";
import StatusChip from "../StatusChip/StatusChip";

// Table filter
const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor("selection", {
    cell: ({ row }) => (
      <div className="">
        <IndeterminateCheckbox
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
    header: ({ table }) => (
      <IndeterminateCheckbox
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    meta: {
      hasIndividualClick: true,
    },
    enableSorting: false,
  }),
  columnHelper.accessor("title", {
    cell: (props) => <RequestTitle row={props.row} value={props.getValue()} />,
    header: "Request Title",
  }),
  columnHelper.accessor("sentOn", {
    cell: (props) => <DateView value={props.getValue()} />,
    header: "Sent On",
    sortType: "datetime",
  }),
  columnHelper.accessor("dueDate", {
    cell: (props) => <DateView value={props.getValue()} />,
    header: "Due Date",
    // sortType: 'datetime',
  }),
  columnHelper.accessor("documentsCount", {
    cell: (props) => <span>{`${props.getValue()} Requested`}</span>,
    header: "Documents",
    // sortType: 'datetime',
  }),
  columnHelper.accessor("status", {
    cell: (props) => <Status value={props.getValue()} row={props.row} />,
    header: "Status",
    enableSorting: false,
  }),
];

export default function useRequestTable(data = []) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );
  const [rowSelection, setRowSelection] = useState({});

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      rowSelection,
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: (newRowSelection) => {
      setRowSelection(newRowSelection);
    },
    debugTable: false,
  });
  const getAllSelectedRowIds = () => {
    return table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row?.original?.id);
  };
  const getAllSelectedRows = () => {
    return table.getFilteredSelectedRowModel().rows.map((u) => u.original);
  };
  const resetAllRowSelection = () => {
    table.resetRowSelection();
  };
  const setPageIndex = (pageIndex) => {
    setPagination((prev) => ({ ...prev, pageIndex }));
  };
  return {
    table,
    globalFilter,
    setGlobalFilter,
    getAllSelectedRowIds,
    pageIndex,
    resetAllRowSelection,
    setPageIndex,
    getAllSelectedRows,
  };
}

// Helper
// Define a custom component for the request title column
function RequestTitle({ row, value }) {
  // Show a rotating arrow icon if the request is recurring
  const icon =
    row.original?.recurringInterval?.trim() !== "" ? (
      <img src={recurringIcon} alt="recurring" className="ml-3" />
    ) : (
      ""
    );
  return (
    <div className="request-title-container">
      <div>{value}</div>
      <div>{icon}</div>
    </div>
  );
}

function DateView({ value }) {
  return <span>{formatISODateToLongDate(value)}</span>;
}

function Status({ value, row }) {
  const { submittedBy = 0, userCount = 0 } = row.original ?? {};
  return (
    <div className="status-description-container">
      <div>{`${submittedBy} of ${userCount}`}</div>
      <StatusChip value={value} />
      <div>
        <img src={rightArrow} alt="right_arrow" />
      </div>
    </div>
  );
}

function IndeterminateCheckbox({ indeterminate, className = "", ...rest }) {
  const ref = React.useRef(null);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate, rest.checked]);

  return (
    <Checkbox
      color="primary"
      indeterminate={indeterminate}
      ref={ref}
      className={"cursor-pointer" + className}
      {...rest}
    />
  );
}
